/**
 * BarcodeScanner.jsx - Scanner de Código de Barras
 * CG Business ERP - P7
 * 
 * DIRECTIVA:
 * - Scanner USB (modo teclado) = input invisível
 * - Scan → Acção imediata (sem modais)
 * - Feedback visual (toast / highlight)
 * - Performance < 300ms
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Barcode, Check, AlertCircle } from 'lucide-react';
import { getStoredAuthToken } from '@/lib/authStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Debounce delay for scanner input (ms)
const SCAN_DEBOUNCE = 150;

// Minimum barcode length
const MIN_CODE_LENGTH = 4;

/**
 * Hook for barcode scanning functionality.
 * Works with USB scanners in keyboard mode.
 * 
 * @param {Object} options
 * @param {Function} options.onScan - Callback when barcode is scanned
 * @param {string} options.locationId - Filter by location (optional)
 * @param {boolean} options.enabled - Enable/disable scanning
 * @param {string} options.mode - 'sale' | 'lookup' | 'inventory'
 */
export function useBarcodeScanner({ 
  onScan, 
  locationId = null, 
  enabled = true,
  mode = 'sale'
}) {
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const bufferRef = useRef('');
  const timeoutRef = useRef(null);
  const inputRef = useRef(null);
  
  const getToken = () => getStoredAuthToken();
  
  // Process scanned code
  const processCode = useCallback(async (code) => {
    if (!code || code.length < MIN_CODE_LENGTH) return;
    
    setScanning(true);
    const startTime = performance.now();
    
    try {
      // Choose endpoint based on mode
      let url = `${API_URL}/barcode/lookup?code=${encodeURIComponent(code)}`;
      
      if (mode === 'sale') {
        url = `${API_URL}/barcode/lookup/for-sale?code=${encodeURIComponent(code)}`;
        if (locationId) {
          url += `&location_id=${encodeURIComponent(locationId)}`;
        }
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      const data = await response.json();
      const elapsed = Math.round(performance.now() - startTime);
      
      setLastScan({
        code,
        found: data.found,
        elapsed,
        timestamp: new Date()
      });
      
      if (data.found) {
        // Success feedback
        if (mode === 'sale' && data.cart_item) {
          toast.success(`${data.cart_item.product_name} adicionado`, {
            description: data.cart_item.serial_number || `${data.cart_item.unit_price?.toLocaleString()} MZN`,
            duration: 2000
          });
        } else if (mode === 'sale' && data.type === 'trackable_product') {
          // Trackable product found but needs individual item scan
          toast.warning(`${data.product.product_name}`, {
            description: data.message || 'Escaneie o IMEI/Nº Série do item específico',
            duration: 4000
          });
          // Don't call onScan for trackable_product - user must scan individual item
          return;
        } else {
          toast.success(`Código encontrado: ${data.data?.product_name || code}`, {
            duration: 2000
          });
        }
        
        onScan?.(data);
      } else {
        // Not found feedback
        const errorMessage = typeof data.message === 'string' ? data.message : 'Código não registado';
        toast.error(errorMessage, {
          description: code,
          duration: 3000
        });
        
        onScan?.({ found: false, code, message: errorMessage });
      }
      
    } catch (error) {
      console.error('[Barcode] Lookup error:', error);
      toast.error('Erro na leitura', {
        description: 'Tente novamente'
      });
      onScan?.({ found: false, code, error: true });
    } finally {
      setScanning(false);
    }
  }, [mode, locationId, onScan]);
  
  // Handle keyboard input (scanner sends keystrokes)
  const handleKeyDown = useCallback((e) => {
    if (!enabled) return;
    
    // Ignore if focus is on a text input
    const activeElement = document.activeElement;
    const isInputFocused = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable
    );
    
    // Allow scanning even when input focused if it's our scanner input
    if (isInputFocused && inputRef.current !== activeElement) {
      return;
    }
    
    // Clear timeout on each keystroke
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Enter key = end of scan
    if (e.key === 'Enter') {
      e.preventDefault();
      const code = bufferRef.current.trim().toUpperCase();
      bufferRef.current = '';
      
      if (code.length >= MIN_CODE_LENGTH) {
        processCode(code);
      }
      return;
    }
    
    // Build buffer with printable characters
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      bufferRef.current += e.key;
      
      // Auto-process after debounce (scanner is fast)
      timeoutRef.current = setTimeout(() => {
        const code = bufferRef.current.trim().toUpperCase();
        bufferRef.current = '';
        
        if (code.length >= MIN_CODE_LENGTH) {
          processCode(code);
        }
      }, SCAN_DEBOUNCE);
    }
  }, [enabled, processCode]);
  
  // Setup global keyboard listener
  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [enabled, handleKeyDown]);
  
  // Manual scan input
  const manualScan = useCallback((code) => {
    if (code && code.length >= MIN_CODE_LENGTH) {
      processCode(code.trim().toUpperCase());
    }
  }, [processCode]);
  
  return {
    scanning,
    lastScan,
    manualScan,
    inputRef
  };
}


/**
 * Visual scanner indicator component.
 * Shows scanning status in UI.
 */
export function ScannerIndicator({ scanning, lastScan, enabled = true }) {
  if (!enabled) return null;
  
  return (
    <div className="flex items-center gap-2 text-sm">
      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full transition-colors ${
        scanning 
          ? 'bg-blue-100 text-blue-700 animate-pulse' 
          : lastScan?.found 
            ? 'bg-green-100 text-green-700'
            : 'bg-slate-100 text-slate-600'
      }`}>
        <Barcode className="h-4 w-4" />
        <span className="font-medium">
          {scanning 
            ? 'A escanear...' 
            : 'Scanner activo'
          }
        </span>
      </div>
      {lastScan && !scanning && (
        <span className="text-xs text-muted-foreground">
          {lastScan.found ? (
            <span className="flex items-center gap-1 text-green-600">
              <Check className="h-3 w-3" />
              {lastScan.elapsed}ms
            </span>
          ) : (
            <span className="flex items-center gap-1 text-red-600">
              <AlertCircle className="h-3 w-3" />
              Não encontrado
            </span>
          )}
        </span>
      )}
    </div>
  );
}


/**
 * Manual barcode input field.
 * For manual entry or testing without scanner.
 */
export function ManualBarcodeInput({ onSubmit, disabled = false, placeholder = 'Digitar código...' }) {
  const [value, setValue] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim().length >= MIN_CODE_LENGTH) {
      onSubmit(value.trim().toUpperCase());
      setValue('');
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="manual-barcode-input"
      />
      <button
        type="submit"
        disabled={disabled || value.trim().length < MIN_CODE_LENGTH}
        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2"
        data-testid="manual-barcode-submit"
      >
        <Barcode className="h-4 w-4" />
      </button>
    </form>
  );
}


export default useBarcodeScanner;
